import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  RegisterConfirmItem,
  cancelRegisterAction,
  confirmRegisterAction,
  undoRegisterAction,
} from '../services/MendsVisionService';
import { fetchRegisterAction, RegisterBulkPreview as BulkDto, RegisterBulkResult } from '../services/aiCompanyService';
import { apiErrorMessage } from '../../utils/apiErrorMessage';
import '../styles/aiCompany.css';

/**
 * F4 — prévia de um comando em lote no Registrar: quem entra (critério legível), quantos, a lista com o que distingue
 * cada lead e quem ficou fora (e por quê). UM clique executa exatamente a lista mostrada; depois mostra o resultado por
 * lead e o desfazer do lote. O follow-up em lote roda em segundo plano: a tela acompanha até terminar.
 */
export interface RegisterBulkPreviewProps {
  sectorId: number;
  requestId: string;
  item: RegisterConfirmItem;
  onUpdate: (item: RegisterConfirmItem) => void;
  /** Intervalo do acompanhamento do lote em andamento (ms). */
  pollMs?: number;
}

const SHOW_FIRST = 8;

const OUTCOME_LABEL: Record<string, string> = {
  done: 'feito',
  queued: 'na fila de Aprovações',
  skipped: 'pulado',
  failed: 'falhou',
  undone: 'desfeito',
  processing: 'em andamento',
};

/** Resultado por lead em português; código novo do servidor nunca aparece cru na tela. */
export function bulkOutcomeLabel(outcome?: string | null): string {
  return OUTCOME_LABEL[(outcome || '').toLowerCase()] ?? 'situação não reconhecida';
}

function confirmLabel(bulk: BulkDto) {
  const n = bulk.count;
  if (bulk.command === 'bulk_follow_up') return `Criar ${n} follow-up${n === 1 ? '' : 's'}`;
  return `Aplicar em ${n} lead${n === 1 ? '' : 's'}`;
}

function groupExclusions(bulk: BulkDto) {
  const groups = new Map<string, { label: string; names: string[] }>();
  for (const e of bulk.excluded || []) {
    const g = groups.get(e.reason) || { label: e.label, names: [] };
    g.names.push(e.name);
    groups.set(e.reason, g);
  }
  return Array.from(groups.values());
}

export const RegisterBulkPreview: React.FC<RegisterBulkPreviewProps> = ({ sectorId, requestId, item, onUpdate, pollMs = 2000 }) => {
  const bulk = item.preview?.bulk as BulkDto | undefined;
  const outcome = (item.outcome || '').toLowerCase();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const updateRef = useRef(onUpdate);
  updateRef.current = onUpdate;
  const base = { sectorId, requestId, actionId: item.actionId };

  // Lote de follow-up em andamento: acompanha o servidor até terminar.
  const running = bulk?.status === 'running';
  // Parou no meio (o servidor caiu/reiniciou): sem acompanhamento, com o que foi feito e o que falta.
  const interrupted = bulk?.status === 'interrupted';
  useEffect(() => {
    if (!running) return undefined;
    let alive = true;
    const timer = window.setInterval(async () => {
      try {
        const next = await fetchRegisterAction(sectorId, requestId, item.actionId);
        if (alive && next) updateRef.current(next);
      } catch {
        // o acompanhamento é só aviso; o servidor continua o lote
      }
    }, pollMs);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [running, sectorId, requestId, item.actionId, pollMs]);

  const exclusions = useMemo(() => (bulk ? groupExclusions(bulk) : []), [bulk]);

  if (!bulk) return null;

  const run = async (fn: () => Promise<RegisterConfirmItem>) => {
    setBusy(true);
    setError(null);
    try {
      onUpdate(await fn());
    } catch (e: unknown) {
      setError(apiErrorMessage(e, 'Não consegui concluir. Nada foi alterado.'));
    } finally {
      setBusy(false);
    }
  };

  const leads = showAll ? bulk.leads : bulk.leads.slice(0, SHOW_FIRST);
  const awaiting = outcome === 'awaiting_confirmation';
  const results: RegisterBulkResult[] = bulk.results || [];
  const progressDone = results.length;

  return (
    <div className="mai-bulk" role="group" aria-label="Comando em lote">
      <p className="mai-bulk-head">
        <strong>{bulk.count}</strong> lead{bulk.count === 1 ? '' : 's'} · {bulk.action}
      </p>

      {bulk.criteria.length > 0 && (
        <div className="mai-bulk-criteria">
          <span className="mai-bulk-label">Quem entra</span>
          <ul>{bulk.criteria.map((c) => <li key={c}>{c}</li>)}</ul>
        </div>
      )}

      {bulk.leads.length > 0 && (awaiting || !results.length) && (
        <div className="mai-bulk-leads">
          <span className="mai-bulk-label">Leads</span>
          <ul>
            {leads.map((l) => (
              <li key={l.contactId}>
                <strong>{l.name}</strong>
                <small>
                  {[
                    l.phoneTail ? `final ${l.phoneTail}` : null,
                    l.column ? `etapa: ${l.column}` : null,
                    l.daysWithoutReply != null ? `sem responder há ${l.daysWithoutReply} dia${l.daysWithoutReply === 1 ? '' : 's'}` : null,
                  ].filter(Boolean).join(' · ')}
                </small>
              </li>
            ))}
          </ul>
          {bulk.leads.length > SHOW_FIRST && (
            <button type="button" className="mai-linkish" onClick={() => setShowAll((v) => !v)}>
              {showAll ? 'Mostrar menos' : `Ver todos os ${bulk.leads.length}`}
            </button>
          )}
        </div>
      )}

      {exclusions.length > 0 && awaiting && (
        <div className="mai-bulk-excluded">
          <span className="mai-bulk-label">Ficaram fora</span>
          <ul>
            {exclusions.map((g) => (
              <li key={g.label}>
                {g.names.length} — {g.label}
                <small> ({g.names.slice(0, 4).join(', ')}{g.names.length > 4 ? '…' : ''})</small>
              </li>
            ))}
          </ul>
        </div>
      )}

      {awaiting && (item.preview?.warnings || []).length > 0 && (
        <ul className="mai-reg-confirm-warn">
          {(item.preview?.warnings || []).map((w) => <li key={w}>{w}</li>)}
        </ul>
      )}

      {awaiting && (
        <div className="mai-btn-row">
          <button type="button" className="mai-btn" disabled={busy || bulk.count === 0} onClick={() => void run(() => confirmRegisterAction(base))}>
            {busy ? 'Executando…' : confirmLabel(bulk)}
          </button>
          <button type="button" className="mai-btn mai-btn-ghost" disabled={busy} onClick={() => void run(() => cancelRegisterAction(base))}>
            Cancelar
          </button>
        </div>
      )}

      {running && (
        <p className="mai-bulk-progress" role="status" aria-live="polite">
          {bulk.command === 'bulk_follow_up'
            ? `Escrevendo os follow-ups: ${progressDone} de ${bulk.count}…`
            : `Aplicando o lote: ${progressDone} de ${bulk.count}…`}
        </p>
      )}

      {interrupted && (
        <p className="mai-bulk-progress" role="status">
          {item.result || `O lote parou no meio: ${progressDone} de ${bulk.count} processados. Peça de novo para os que faltaram.`}
        </p>
      )}

      {!awaiting && results.length > 0 && (
        <div className="mai-bulk-results">
          <button type="button" className="mai-linkish" onClick={() => setShowResults((v) => !v)}>
            {showResults ? 'Esconder o resultado por lead' : 'Ver o resultado por lead'}
          </button>
          {showResults && (
            <ul>
              {results.map((r) => (
                <li key={r.contactId} className={`mai-bulk-r-${r.outcome}`}>
                  <strong>{r.name}</strong>: {bulkOutcomeLabel(r.outcome)}
                  {r.detail ? <small> — {r.detail}</small> : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {outcome === 'executed' && item.canUndo && bulk.status !== 'undone' && (
        <div className="mai-btn-row" style={{ marginTop: 4 }}>
          <button type="button" className="mai-linkish" disabled={busy} onClick={() => void run(() => undoRegisterAction(base))}>
            {busy ? 'Desfazendo…' : 'Desfazer o lote'}
          </button>
        </div>
      )}
      {error && <p className="mai-form-error" role="alert">{error}</p>}
    </div>
  );
};

/** F4 — "Entendi assim": a preferência dita em frase, com o que foi assumido, e UM clique para gravar. */
export const RegisterPreferenceConfirm: React.FC<Omit<RegisterBulkPreviewProps, 'pollMs'>> = ({ sectorId, requestId, item, onUpdate }) => {
  const draft = item.preview?.preference;
  const outcome = (item.outcome || '').toLowerCase();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!draft) return null;
  const base = { sectorId, requestId, actionId: item.actionId };
  const run = async (fn: () => Promise<RegisterConfirmItem>) => {
    setBusy(true);
    setError(null);
    try {
      onUpdate(await fn());
    } catch (e: unknown) {
      setError(apiErrorMessage(e, 'Não consegui concluir. Nada foi gravado.'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mai-bulk mai-pref-confirm" role="group" aria-label="Preferência de envio">
      <p className="mai-bulk-head">Entendi assim:</p>
      <ul className="mai-pref-lines">{draft.lines.map((l) => <li key={l}>{l}</li>)}</ul>
      <p className="mai-pref-phrase">Frase: “{draft.phrase}”</p>
      {outcome === 'awaiting_confirmation' && (
        <>
          {(item.preview?.warnings || []).length > 0 && (
            <ul className="mai-reg-confirm-warn">{(item.preview?.warnings || []).map((w) => <li key={w}>{w}</li>)}</ul>
          )}
          <div className="mai-btn-row">
            <button type="button" className="mai-btn" disabled={busy} onClick={() => void run(() => confirmRegisterAction(base))}>
              {busy ? 'Salvando…' : 'Está certo, salvar'}
            </button>
            <button type="button" className="mai-btn mai-btn-ghost" disabled={busy} onClick={() => void run(() => cancelRegisterAction(base))}>
              Cancelar
            </button>
          </div>
        </>
      )}
      {outcome === 'executed' && item.canUndo && (
        <div className="mai-btn-row" style={{ marginTop: 4 }}>
          <button type="button" className="mai-linkish" disabled={busy} onClick={() => void run(() => undoRegisterAction(base))}>
            Desfazer (apagar a preferência)
          </button>
        </div>
      )}
      {error && <p className="mai-form-error" role="alert">{error}</p>}
    </div>
  );
};
